"use client";
import CustomInput from "@/components/shared/CustomInput";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import name from "@/public/images/nameContact.svg";
import phoneContact from "@/public/images/phoneContact.svg";
import emailContact from "@/public/images/emailContact.svg";
import message from "@/public/images/message.svg";
import Image from "next/image";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { useCreateContactMutation } from "@/redux/features/api/dashboard/contactUsApiSlice";
import { toast } from "react-toastify";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getErrorMessage } from "@/utils/error/getErrorMessage";
import * as Yup from "yup";

const translations = {
  ar: {
    contactUs: "تواصل معنا",
    contactUsNow: "تواصل معنا الان",
    loremIpsum:
      "في سفير الجامعات، نحن هنا لدعمك في رحلتك التعليمية. تواصل معنا لأي استفسار، ودع فريقنا المختص يساعدك في اتخاذ القرار الصحيح",
    placeholders: {
      fullName: "اسمك بالكامل",
      phone: "رقم الهاتف",
      email: "الإيميل الخاص بك",
      message: "رسالتك",
    },
    submitButton: "تواصل معنا",
    cancelButton: "الغاء",
  },
  en: {
    contactUs: "Contact Us",
    contactUsNow: "Contact Us Now",
    loremIpsum:
      "In Safir Universities, we are here to support you on your educational journey. Reach out to us for any inquiries, and let our specialized team assist you in making the right decision.",
    placeholders: {
      fullName: "Your Full Name",
      phone: "Phone Number",
      email: "Your Email",
      message: "Your Message",
    },
    submitButton: "Contact Us",
    cancelButton: "Cancel",
  },
};
type Locale = "ar" | "en";

const Index = () => {
  const { locale }: { locale: Locale } = useTranslate();
  const [createContact, { isLoading, isSuccess, error }] =
    useCreateContactMutation();
  const t = translations[locale];

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(locale === "ar" ? "الاسم مطلوب" : "Name is required"),
    phone: Yup.string().required(locale === "ar" ? "رقم الهاتف مطلوب" : "Phone number is required"),
    email: Yup.string()
      .email(locale === "ar" ? "البريد الإلكتروني غير صالح" : "Invalid email format")
      .required(locale === "ar" ? "البريد الإلكتروني مطلوب" : "Email is required"),
    message: Yup.string().required(locale === "ar" ? "الرسالة مطلوبة" : "Message is required"),
  });

  useEffect(() => {
    if (isSuccess) {
      toast.success("تم ارسال الرسالة بنجاح");
    }
    if (error && !isSuccess) {
      toast.error(getErrorMessage(error, locale));
    }
  }, [isSuccess, error]);

  return (
    <div className="bg-white">
      <div className="container py-[100px]">
        <p className="text-20 text-primaryColor w-fit mx-auto py-1 px-7 border border-primaryColor rounded-full mb-2 flex items-center gap-3">
          {t.contactUs}
        </p>
        <p className="text-25 text-darkText font-[600] text-center mb-5">
          {t.contactUsNow}
        </p>
        <p className="text-20 text-[#6C7278] text-center max-w-[723px] mx-auto mb-10">
          {t.loremIpsum}
        </p>
        <Formik
          initialValues={{ name: "", phone: "", email: "", message: "" }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            await createContact(values);
            if (!error) resetForm();
          }}
        >
          {(props) => (
            <Form className="flex flex-col gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative" style={{}}>
                  <Field
                    type="text"
                    name="name"
                    className="h-[70px] border border-[#EBEBEB] rounded-[10px] px-14 w-full flex items-center placeholder:text-14 placeholder:text-descriptionText outline-none"
                    placeholder={t.placeholders.fullName}
                  />
                  <Image
                    src={name}
                    alt="name"
                    className={`absolute ${
                      locale === "en" ? "left-3" : " right-3"
                    } top-1/2 -translate-y-1/2`}
                  />
                </div>
                <div className="relative">
                  <Field
                    type="text"
                    name="phone"
                    className="h-[70px] border border-[#EBEBEB] rounded-[10px] px-14 w-full flex items-center placeholder:text-14 placeholder:text-descriptionText outline-none"
                    placeholder={t.placeholders.phone}
                  />
                  <Image
                    src={phoneContact}
                    alt="phone"
                    className={`absolute ${
                      locale === "en" ? "left-3" : " right-3"
                    } top-1/2 -translate-y-1/2`}
                  />
                </div>
              </div>
              <div className="relative">
                <Field
                  type="email"
                  name="email"
                  className="h-[70px] border border-[#EBEBEB] rounded-[10px] px-14 w-full flex items-center placeholder:text-14 placeholder:text-descriptionText outline-none"
                  placeholder={t.placeholders.email}
                />
                <Image
                  src={emailContact}
                  alt="email"
                  className={` ${
                    locale === "en" ? "left-3" : " right-3"
                  } absolute  top-1/2 -translate-y-1/2`}
                />
              </div>
              <div className="relative">
                <Field
                  as="textarea"
                  name="message"
                  className="min-h-[12rem] border border-[#EBEBEB] rounded-[10px] px-14 py-5 w-full flex items-center placeholder:text-14 placeholder:text-descriptionText outline-none"
                  placeholder={t.placeholders.message}
                />
                <Image
                  src={message}
                  alt="message"
                  className={` ${
                    locale === "en" ? "left-3" : " right-3"
                  } absolute  top-5`}
                />
              </div>

              {/* Show validation errors if any */}
              {Object.keys(props.errors).length > 0 && props.submitCount > 0 && (
                <div className="text-red-500 text-sm">
                  {Object.values(props.errors)[0] as string}
                </div>
              )}

              <div className="flex gap-5">
                <button
                  disabled={isLoading}
                  type="submit"
                  className="text-16 text-white h-[56px] bg-primaryColor px-10 rounded-[10px] grow"
                >
                  {t.submitButton}
                </button>
                <p className="text-16 text-darkText flex items-center justify-center h-[56px] bg-[#F8F9FA] px-16 rounded-[10px]">
                  {t.cancelButton}
                </p>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Index;
